import { postError, postSuccess, postWarning } from '@/helpers/notification'
import { PdfImportController } from '@/controllers/admin/pdfImport'
import {
  BookTarget,
  BulkDuplicatePagesResponse,
  ContentTypeFilter,
  MergePageResponse,
  PdfImportOverviewParams,
  PdfImportPageTile,
  PdfImportStatus,
  PublishSelectedRequest,
  ReviewStatus
} from '@/interfaces/admin/pdfImport'
import { Module } from 'vuex'
import i18n from '@/locales'
import router from '@/router'

const DEFAULT_REVIEW_STATUS_FILTER: ReviewStatus[] = ['APPROVED', 'PENDING', 'NO_STATUS']

export interface PdfImportOverviewState {
  // Core identifiers and metadata
  importId: number | null
  importName: string
  importLocale: string
  sourceFilename: string
  workflowStatus: PdfImportStatus | null
  locked: boolean

  // Data collections
  pages: PdfImportPageTile[]
  selectedPageIds: Set<number>
  filteredPageCount: number
  totalCount: number

  // Filters
  filters: {
    contentType: ContentTypeFilter[]
    incompleteBomOnly: boolean
    pageNumberFilter: string
    reviewStatus: ReviewStatus[]
    search: string
  }
  pageNumberFilterError: boolean

  // Pagination
  pagination: {
    hasMore: boolean
    limit: number
    offset: number
  }

  // Loading states
  autoMerging: boolean
  loading: boolean
  loadingMore: boolean
  loadingSelectAll: boolean
  merging: boolean

  scrollAnchorOffset: number | null

  // When true, initialize() forces a full state reset (filters, pages, selection) on reload.
  // Used after publish — a hard operation where starting clean is appropriate.
  isStale: boolean

  // When true, initialize() reloads page data while preserving active filters.
  // Used after reset operations where the user's filter context should survive.
  isPagesStale: boolean

  // Progress tracking
  publishProgress: any | null
  serverReviewProgress: {
    approvedCount: number
    droppedCount: number
    pendingCount: number
  } | null
}

const initialState: PdfImportOverviewState = {
  // Core identifiers and metadata
  importId: null,
  importName: '',
  importLocale: '',
  sourceFilename: '',
  workflowStatus: null,
  locked: false,

  // Data collections
  pages: [],
  selectedPageIds: new Set(),
  filteredPageCount: 0,
  totalCount: 0,

  // Filters
  filters: {
    contentType: [],
    incompleteBomOnly: false,
    pageNumberFilter: '',
    reviewStatus: [...DEFAULT_REVIEW_STATUS_FILTER],
    search: ''
  },
  pageNumberFilterError: false,

  // Pagination
  pagination: {
    hasMore: false,
    limit: 100,
    offset: 0
  },

  // Loading states
  autoMerging: false,
  loading: false,
  loadingMore: false,
  loadingSelectAll: false,
  merging: false,

  scrollAnchorOffset: null,

  isStale: false,
  isPagesStale: false,

  // Progress tracking
  publishProgress: null,
  serverReviewProgress: null
}

const pdfImportOverview: Module<PdfImportOverviewState, any> = {
  namespaced: true,

  state: { ...initialState },

  getters: {
    canLoadMore: (state) => state.pagination.hasMore,
    filteredPageCount: (state) => state.filteredPageCount,
    importLocale: (state) => state.importLocale,
    importName: (state) => state.importName,
    isAllSelected: (state) => {
      const filteredCount = state.filteredPageCount
      const selectedCount = state.selectedPageIds?.size ?? 0
      return filteredCount > 0 && selectedCount === filteredCount
    },
    isFiltersAtDefault: (state) => {
      const sameReviewStatus =
        state.filters.reviewStatus.length === DEFAULT_REVIEW_STATUS_FILTER.length &&
        DEFAULT_REVIEW_STATUS_FILTER.every(s => state.filters.reviewStatus.includes(s))
      return (
        !state.filters.search &&
        !state.filters.pageNumberFilter &&
        !state.filters.incompleteBomOnly &&
        sameReviewStatus &&
        state.filters.contentType.length === 0
      )
    },
    autoMerging: (state) => state.autoMerging,
    isLocked: (state) => state.locked,
    isNoneSelected: (state) => (state.selectedPageIds?.size ?? 0) === 0,
    isSomeSelected: (state) => {
      const selectedCount = state.selectedPageIds?.size ?? 0
      const filteredCount = state.filteredPageCount
      return selectedCount > 0 && selectedCount < filteredCount
    },
    loading: (state) => state.loading,
    loadingMore: (state) => state.loadingMore,
    loadingSelectAll: (state) => state.loadingSelectAll,
    merging: (state) => state.merging,
    reviewProgress: (state) => {
      // Use server-side counts if available (accurate across all pages)
      if (state.serverReviewProgress) {
        return {
          approved: state.serverReviewProgress.approvedCount,
          dropped: state.serverReviewProgress.droppedCount,
          pending: state.serverReviewProgress.pendingCount
        }
      }
      // Fallback to client-side calculation from loaded pages
      const approved = state.pages.filter(p => p.reviewStatus === 'APPROVED').length
      const pending = state.pages.filter(p => p.reviewStatus === 'PENDING').length
      const dropped = state.pages.filter(p => p.reviewStatus === 'DROPPED').length
      return { approved, dropped, pending }
    },
    selectedCount: (state) => state.selectedPageIds?.size ?? 0,
    selectedPageIds: (state) => state.selectedPageIds,
    sourceMergePageNumbers: (state): number[] => {
      if (state.selectedPageIds.size < 2) return []
      const selected = state.pages.filter(p => state.selectedPageIds.has(p.pageId))
      const sorted = selected.map(p => p.pageNumber).sort((a, b) => a - b)
      const [, ...sources] = sorted
      return sources
    },
    sourceFilename: (state) => state.sourceFilename,
    targetMergePageNumber: (state): number | null => {
      if (state.selectedPageIds.size < 2) return null
      const selected = state.pages.filter(p => state.selectedPageIds.has(p.pageId))
      return selected.length > 0 ? Math.min(...selected.map(p => p.pageNumber)) : null
    },
    totalPages: (state) => state.totalCount,
    visiblePages: (state) => state.pages,
    workflowStatus: (state) => state.workflowStatus
  },

  mutations: {
    APPLY_MERGE_RESULT(state, response: MergePageResponse) {
      // Update server-side review progress counts
      state.serverReviewProgress = {
        approvedCount: response.reviewProgress.approvedCount,
        droppedCount: response.reviewProgress.droppedCount,
        pendingCount: response.reviewProgress.pendingCount
      }

      // Iterate backwards to safely remove items
      for (let i = state.pages.length - 1; i >= 0; i--) {
        const page = state.pages[i]
        if (page.pageId === response.targetPageId) {
          // Update target page with merged data
          state.pages[i] = {
            ...page,
            bomItemCount: response.targetBomItemCount,
            composed: response.composed,
            illustrationCount: response.targetIllustrationCount
          }
        } else if (response.droppedPageIds.includes(page.pageId)) {
          // Check if DROPPED status matches current filter
          const matchesFilter = state.filters.reviewStatus.length === 0 ||
            state.filters.reviewStatus.includes('DROPPED')

          if (matchesFilter) {
            // Update in place
            state.pages[i] = { ...page, reviewStatus: 'DROPPED' as ReviewStatus }
          } else {
            // Remove from view (no longer matches filter)
            const pageId = state.pages[i].pageId
            state.pages.splice(i, 1)
            if (state.selectedPageIds instanceof Set && state.selectedPageIds.has(pageId)) {
              const updated = new Set(state.selectedPageIds)
              updated.delete(pageId)
              state.selectedPageIds = updated
            }
            state.filteredPageCount = Math.max(0, state.filteredPageCount - 1)
          }
        }
      }
    },

    DESELECT_ALL_PAGES(state) {
      state.selectedPageIds = new Set()
    },

    MARK_STALE(state) {
      state.isStale = true
    },

    MARK_PAGES_STALE(state) {
      state.isPagesStale = true
    },

    FINISH_LOADING(state) {
      state.loading = false
      state.loadingMore = false
    },

    FINISH_LOADING_SELECT_ALL(state) {
      state.loadingSelectAll = false
    },

    RESET_PAGES_AND_PAGINATION(state) {
      state.pages = []
      state.selectedPageIds = new Set()
      state.filteredPageCount = 0
      state.totalCount = 0
      state.pagination = { hasMore: false, limit: state.pagination.limit, offset: 0 }
      state.scrollAnchorOffset = null
      state.isPagesStale = false
      state.serverReviewProgress = null
      state.publishProgress = null
      state.loading = false
      state.loadingMore = false
      state.loadingSelectAll = false
      state.merging = false
      state.autoMerging = false
      state.pageNumberFilterError = false
    },

    RESET_STATE(state) {
      Object.assign(state, JSON.parse(JSON.stringify(initialState)))
    },

    SELECT_ALL_PAGES(state) {
      state.selectedPageIds = new Set(state.pages.map(p => p.pageId))
    },

    SELECT_ALL_PAGES_WITH_IDS(state, pageIds: number[]) {
      state.selectedPageIds = new Set(pageIds)
    },

    SET_IMPORT_ID(state, importId: number) {
      state.importId = importId
    },

    SET_AUTO_MERGING(state, autoMerging: boolean) {
      state.autoMerging = autoMerging
    },

    SET_SCROLL_ANCHOR(state, offset: number | null) {
      state.scrollAnchorOffset = offset
    },

    SET_MERGING(state, merging: boolean) {
      state.merging = merging
    },

    SET_OVERVIEW_DATA(state, {
      importName,
      sourceFilename,
      workflowStatus,
      locked,
      pages,
      filteredPageCount,
      totalPages,
      defaultTranslationLocale,
      publishProgress,
      reset = false
    }) {
      state.importName = importName
      state.sourceFilename = sourceFilename
      state.workflowStatus = workflowStatus
      state.locked = locked
      state.filteredPageCount = filteredPageCount
      state.totalCount = totalPages

      if (defaultTranslationLocale !== undefined) {
        state.importLocale = defaultTranslationLocale
      }

      if (publishProgress !== undefined) {
        state.publishProgress = publishProgress
      }

      if (reset) {
        state.pages.splice(0, state.pages.length, ...pages)
      } else {
        state.pages.push(...pages)
      }
    },

    ADJUST_SERVER_REVIEW_PROGRESS(state, { oldStatus, newStatus }: { oldStatus: string | null, newStatus: string | null }) {
      if (!state.serverReviewProgress) return
      const p = { ...state.serverReviewProgress }
      const apply = (status: string | null, delta: number) => {
        if (status === 'APPROVED') p.approvedCount = Math.max(0, p.approvedCount + delta)
        else if (status === 'PENDING') p.pendingCount = Math.max(0, p.pendingCount + delta)
        else if (status === 'DROPPED') p.droppedCount = Math.max(0, p.droppedCount + delta)
      }
      apply(oldStatus, -1)
      apply(newStatus, +1)
      state.serverReviewProgress = p
    },

    SET_SERVER_REVIEW_PROGRESS(state, progress: { approvedCount: number, droppedCount: number, pendingCount: number }) {
      state.serverReviewProgress = progress
    },

    START_LOADING(state) {
      state.loading = true
      // Don't clear pages - update in-place to preserve scroll position
    },

    START_LOADING_MORE(state) {
      state.loadingMore = true
    },

    START_LOADING_SELECT_ALL(state) {
      state.loadingSelectAll = true
    },

    TOGGLE_PAGE_SELECTION(state, pageId: number) {
      // Ensure selectedPageIds is a Set
      if (!(state.selectedPageIds instanceof Set)) {
        state.selectedPageIds = new Set()
      }

      if (state.selectedPageIds.has(pageId)) {
        state.selectedPageIds.delete(pageId)
      } else {
        state.selectedPageIds.add(pageId)
      }
      // Create new Set to trigger reactivity
      state.selectedPageIds = new Set(state.selectedPageIds)
    },

    UPDATE_CONTENT_TYPE_FILTER(state, contentType: ContentTypeFilter[]) {
      state.filters.contentType = contentType
      state.pagination.offset = 0
    },

    UPDATE_INCOMPLETE_BOM_ONLY(state, value: boolean) {
      state.filters.incompleteBomOnly = value
      state.pagination.offset = 0
    },

    UPDATE_PAGE_NUMBER_FILTER(state, value: string) {
      state.filters.pageNumberFilter = value
      state.pageNumberFilterError = false
      state.pagination.offset = 0
    },

    SET_PAGE_NUMBER_FILTER_ERROR(state, hasError: boolean) {
      state.pageNumberFilterError = hasError
    },

    UPDATE_PAGE_DATA(state, { pageId, updates }: { pageId: number, updates: Partial<PdfImportPageTile> }) {
      const index = state.pages.findIndex(page => page.pageId === pageId)
      if (index !== -1) {
        state.pages[index] = { ...state.pages[index], ...updates }
      }
      // Note: If page not loaded, update will be applied when overview reloads
    },

    UPDATE_PAGE_REVIEW_STATUS(state, { pageIds, reviewStatus }: { pageIds: number[], reviewStatus: ReviewStatus | null }) {
      // Normalize: 'NO_STATUS' string → null, undefined → null
      const normalizedStatus = reviewStatus === 'NO_STATUS' || reviewStatus === undefined ? null : reviewStatus

      // Iterate backwards to safely remove items
      for (let i = state.pages.length - 1; i >= 0; i--) {
        if (pageIds.includes(state.pages[i].pageId)) {
          // Check if updated status matches current filter
          const filterLength = state.filters.reviewStatus.length
          const statusToCheck = normalizedStatus === null ? 'NO_STATUS' : normalizedStatus
          const matchesFilter = filterLength === 0 ||
            state.filters.reviewStatus.includes(statusToCheck)

          if (matchesFilter) {
            // Update in place
            state.pages[i] = {
              ...state.pages[i],
              reviewStatus: normalizedStatus
            }
          } else {
            // Remove from view (no longer matches filter)
            const pageId = state.pages[i].pageId
            state.pages.splice(i, 1)
            if (state.selectedPageIds instanceof Set && state.selectedPageIds.has(pageId)) {
              const updated = new Set(state.selectedPageIds)
              updated.delete(pageId)
              state.selectedPageIds = updated
            }
            state.filteredPageCount = Math.max(0, state.filteredPageCount - 1)
          }
        }
      }
    },

    UPDATE_PAGINATION(state, { offset, limit, hasMore }) {
      state.pagination.offset = offset
      state.pagination.limit = limit || state.pagination.limit
      state.pagination.hasMore = hasMore
    },

    UPDATE_REVIEW_STATUS_FILTER(state, reviewStatus: ReviewStatus[]) {
      state.filters.reviewStatus = reviewStatus
      state.pagination.offset = 0
    },

    UPDATE_SEARCH(state, search: string) {
      state.filters.search = search
      state.pagination.offset = 0
    }
  },

  actions: {
    async deleteImport({ state, commit }) {
      if (!state.importId) return

      try {
        await PdfImportController.deletePdfImport(state.importId)
        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.t('pdfImportOverview.deleteSuccessToast', { importName: state.importName })
        })
        // Invalidate the module before navigating away so that browser Back can't land on
        // initialize()'s no-reload branch and re-render the now-deleted import from stale
        // Vuex state. Use replace (not push) so Back skips this Overview entirely.
        commit('RESET_STATE')
        router.replace({ name: 'PdfImportsManager' })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportOverview.deleteFailed')
        })
        throw error
      }
    },

    async copyToFirstSelected({ state, commit }) {
      const pageIds = Array.from(state.selectedPageIds)
      if (pageIds.length < 2 || !state.importId) return

      commit('SET_MERGING', true)
      const start = Date.now()

      try {
        const response = await PdfImportController.mergePages(state.importId, { pageIds })

        const elapsed = Date.now() - start
        if (elapsed < 400) await new Promise<void>(r => setTimeout(r, 400 - elapsed))

        commit('APPLY_MERGE_RESULT', response)

        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.t('pdfImportOverview.mergeSuccess', {
            bomCount: response.targetBomItemCount,
            illustrationCount: response.targetIllustrationCount,
            pageLabel: `${i18n.global.tc('page', 1)} ${response.targetPageNumber}`
          })
        })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportOverview.mergeFailed')
        })
      } finally {
        commit('SET_MERGING', false)
        commit('DESELECT_ALL_PAGES')
      }
    },

    async duplicateSelectedPages({ state, commit, dispatch }) {
      const pageIds = Array.from(state.selectedPageIds)
      if (pageIds.length === 0 || !state.importId) return

      let response: BulkDuplicatePagesResponse
      try {
        response = await PdfImportController.duplicatePages(state.importId, { pageIds })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportOverview.duplicateFailed')
        })
        throw error
      }

      if (response.successCount === 0) {
        // Every selected page failed (e.g., missing illustration source files) even though the
        // request itself returned 200 OK with a populated failedPageIds. This is a genuine
        // failure, not a partial success — show the standard error notification (matching the
        // catch block above) and keep the selection intact so the user can retry without
        // reselecting everything. Nothing changed server-side, so no grid reload is needed.
        postError({
          title: i18n.global.t('error'),
          text: i18n.global.t('pdfImportOverview.duplicateFailed')
        })
        return
      }

      // Duplication itself succeeded (even if partial) — report that outcome now, independent
      // of whether the grid refresh below succeeds. A refresh failure must never look like the
      // duplication silently did nothing when it actually, durably, did something.
      commit('DESELECT_ALL_PAGES')

      if (response.failedPageIds.length > 0) {
        postWarning({
          title: i18n.global.t('warning'),
          text: i18n.global.t('pdfImportOverview.duplicatePartialToast', {
            successCount: response.successCount,
            totalCount: response.successCount + response.failedPageIds.length,
            failedCount: response.failedPageIds.length
          })
        })
      } else {
        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.tc('pdfImportOverview.duplicateSuccessToast', response.successCount, { count: response.successCount })
        })
      }

      // Reload from the server rather than splicing the response in-place — DuplicatePageResponse
      // doesn't carry a thumbnailUrl, so a spliced tile would render with a broken/placeholder
      // thumbnail until the next reload anyway. This also keeps state correct on a partial failure,
      // where some of the requested pages were actually duplicated server-side despite the error toast.
      // Handled in its own try/catch: loadOverview re-throws on non-400 errors, and letting that
      // propagate here would surface as an empty catch in the component — silent, even though the
      // duplication above already succeeded and the grid is now stale.
      try {
        await dispatch('loadOverview', { reset: true })
      } catch (error: any) {
        commit('MARK_PAGES_STALE')
        postWarning({
          title: i18n.global.t('warning'),
          text: i18n.global.t('pdfImportOverview.duplicateRefreshFailed')
        })
      }
    },

    async autoMergePages({ state, commit, dispatch }): Promise<void> {
      if (!state.importId) throw new Error('No import ID')

      commit('SET_AUTO_MERGING', true)
      try {
        const result = await PdfImportController.autoMergePages(state.importId)
        await dispatch('loadOverview', { reset: true })
        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.t('pdfImportOverview.autoMergeSuccess', {
            count: result.totalMergesPerformed,
            dropped: result.totalPagesDropped
          })
        })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportOverview.autoMergeFailed')
        })
      } finally {
        commit('SET_AUTO_MERGING', false)
        commit('DESELECT_ALL_PAGES')
      }
    },

    async initialize({ commit, dispatch, state }, importId: number) {
      if (state.importId !== importId) {
        // Different import — full reset and reload
        commit('RESET_STATE')
        commit('SET_IMPORT_ID', importId)
        await dispatch('loadOverview', { reset: true })
      } else if (state.isStale) {
        // Full reset — data is stale after a hard operation (e.g. publish).
        commit('RESET_STATE')
        commit('SET_IMPORT_ID', importId)
        await dispatch('loadOverview', { reset: true })
      } else if (state.isPagesStale || state.pages.length === 0) {
        // Soft reset — reload page data while preserving active filters.
        // Used after reset operations and on first load of a known import.
        commit('RESET_PAGES_AND_PAGINATION')
        commit('SET_IMPORT_ID', importId)
        await dispatch('loadOverview', { reset: true })
      } else {
        // Same import with pages already loaded — returning from page details or tab switch.
        // Preserve pages, filters, and selection; scroll anchor restored by grid on ready.
        commit('SET_IMPORT_ID', importId)
      }
    },

    async loadMore({ state, dispatch }) {
      if (!state.pagination.hasMore || state.loadingMore) return
      await dispatch('loadOverview', { offset: state.pagination.offset })
    },

    async loadOverview({ state, commit }, { offset, reset = false } = {}) {
      try {
        if (reset) {
          commit('START_LOADING')
        } else {
          commit('START_LOADING_MORE')
        }

        const currentOffset = offset !== undefined ? offset : (reset ? 0 : state.pagination.offset)

        const params: PdfImportOverviewParams = {
          limit: state.pagination.limit,
          offset: currentOffset,
          q: state.filters.search || undefined,
          reviewStatus: state.filters.reviewStatus.length > 0 ? state.filters.reviewStatus : undefined,
          contentType: state.filters.contentType.length > 0 ? state.filters.contentType : undefined,
          pageNumberFilter: state.filters.pageNumberFilter || undefined,
          incompleteBomOnly: state.filters.incompleteBomOnly || undefined
        }

        const response = await PdfImportController.getOverview(state.importId!, params)

        commit('SET_OVERVIEW_DATA', {
          importName: response.importName,
          sourceFilename: response.sourceFilename,
          workflowStatus: response.workflowStatus,
          locked: response.locked,
          publishProgress: response.publishProgress,
          pages: response.pages,
          filteredPageCount: response.filteredPageCount,
          totalPages: response.totalPages,
          defaultTranslationLocale: response.defaultTranslationLocale,
          reset
        })

        commit('SET_SERVER_REVIEW_PROGRESS', response.reviewStatusCounts)

        const hasMore = response.pages.length >= state.pagination.limit &&
                       currentOffset + response.pages.length < response.filteredPageCount
        commit('UPDATE_PAGINATION', {
          offset: currentOffset + response.pages.length,
          limit: state.pagination.limit,
          hasMore
        })

        commit('FINISH_LOADING')
        return response
      } catch (error: any) {
        commit('FINISH_LOADING')
        if (error?.response?.status === 400) {
          commit('SET_PAGE_NUMBER_FILTER_ERROR', true)
        } else {
          throw error
        }
      }
    },

    async publishSelected({ state, commit }, { skipAutoHotpointing, bookTarget }: { skipAutoHotpointing: boolean; bookTarget: BookTarget | null }) {
      const pageIds = Array.from(state.selectedPageIds)
      if (pageIds.length === 0 || !state.importId) return

      const request: PublishSelectedRequest = { pageIds, skipAutoHotpointing, bookTarget }

      try {
        await PdfImportController.publishSelected(state.importId, request)
        commit('MARK_STALE')
        router.push({ name: 'PdfImportsManager' })
      } catch (error: any) {
        const status = error?.response?.status
        const message = error?.response?.data?.message

        if (status === 409 && message === 'Book Identifier must be unique.') {
          postError({
            title: i18n.global.t('error'),
            text: i18n.global.t('pdfImportOverview.publishBookIdentifierNotUnique')
          })
        } else if (status === 423) {
          postError({
            title: i18n.global.t('error'),
            text: i18n.global.t('pdfImport.lockedImportMessage')
          })
        } else {
          postError({
            title: i18n.global.t('error'),
            text: message ?? i18n.global.t('pdfImportOverview.publishFailed')
          })
        }
        throw error
      }
    },

    async resetSelectedPages({ state, commit }) {
      const pageIds = Array.from(state.selectedPageIds)
      if (pageIds.length === 0 || !state.importId) return

      try {
        await PdfImportController.resetPages(state.importId, { pageIds })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : (error?.response?.data?.message ?? i18n.global.t('pdfImportOverview.resetErrorToast'))
        })
        throw error
      }

      // 202 received — clear selection, mark stale, redirect to manager.
      // Full reset on return: the manager→overview path always reloads from a clean slate.
      commit('DESELECT_ALL_PAGES')
      commit('MARK_STALE')
      router.push({ name: 'PdfImportsManager' })
    },

    togglePageSelection({ commit }, pageId: number) {
      commit('TOGGLE_PAGE_SELECTION', pageId)
    },

    async toggleSelectAll({ commit, state }, selectAll: boolean) {
      if (selectAll) {
        try {
          commit('START_LOADING_SELECT_ALL')

          const params: PdfImportOverviewParams = {
            q: state.filters.search || undefined,
            reviewStatus: state.filters.reviewStatus.length > 0 ? state.filters.reviewStatus : undefined,
            contentType: state.filters.contentType.length > 0 ? state.filters.contentType : undefined,
            pageNumberFilter: state.filters.pageNumberFilter || undefined,
            incompleteBomOnly: state.filters.incompleteBomOnly || undefined
          }

          const response = await PdfImportController.getPageIds(state.importId!, params)
          commit('SELECT_ALL_PAGES_WITH_IDS', response.pageIds)
          commit('FINISH_LOADING_SELECT_ALL')
        } catch (error) {
          commit('FINISH_LOADING_SELECT_ALL')
          postError({
            title: i18n.global.t('error'),
            text: i18n.global.t('pdfImportOverview.notificationSelectAllFailed')
          })
          throw error
        }
      } else {
        commit('DESELECT_ALL_PAGES')
      }
    },

    async updateContentTypeFilter({ commit, dispatch }, contentType: ContentTypeFilter[]) {
      commit('START_LOADING')
      commit('DESELECT_ALL_PAGES')
      commit('UPDATE_CONTENT_TYPE_FILTER', contentType)
      await dispatch('loadOverview', { reset: true })
    },

    async updateIncompleteBomOnly({ commit, dispatch }, value: boolean) {
      commit('START_LOADING')
      commit('DESELECT_ALL_PAGES')
      commit('UPDATE_INCOMPLETE_BOM_ONLY', value)
      await dispatch('loadOverview', { reset: true })
    },

    async updatePageNumberFilter({ commit, dispatch }, value: string) {
      commit('START_LOADING')
      commit('DESELECT_ALL_PAGES')
      commit('UPDATE_PAGE_NUMBER_FILTER', value)
      await dispatch('loadOverview', { reset: true })
    },

    async updateReviewStatus({ state, commit }, { status }: { status: ReviewStatus }) {
      const pageIds = Array.from(state.selectedPageIds)
      if (pageIds.length === 0) return

      try {
        const response = await PdfImportController.updatePageReviewStatus(state.importId!, pageIds, status)

        // Update server-side review progress counts
        commit('SET_SERVER_REVIEW_PROGRESS', response.reviewProgress)

        // Update review status in-place and remove pages that don't match filter
        commit('UPDATE_PAGE_REVIEW_STATUS', { pageIds, reviewStatus: status })

        // Clear selection
        commit('DESELECT_ALL_PAGES')

        // Show success notification
        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.t('pdfImportOverview.notificationReviewStatusUpdated', { count: response.updatedCount })
        })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportOverview.notificationUpdateReviewStatusFailed')
        })
        throw error
      }
    },

    async updateReviewStatusFilter({ commit, dispatch }, reviewStatus: ReviewStatus[]) {
      commit('START_LOADING')
      commit('DESELECT_ALL_PAGES')
      commit('UPDATE_REVIEW_STATUS_FILTER', reviewStatus)
      await dispatch('loadOverview', { reset: true })
    },

    async updateSearch({ commit, dispatch }, searchTerm: string) {
      commit('START_LOADING')
      commit('DESELECT_ALL_PAGES')
      commit('UPDATE_SEARCH', searchTerm)
      await dispatch('loadOverview', { reset: true })
    }
  }
}

export default pdfImportOverview
